/**
 * AI Suggestion Modal Component
 * Modal for generating shopping list using Groq AI (via backend proxy)
 */

import React, { useState } from 'react';
import { X, Sparkles, Loader, Zap } from 'lucide-react';
import { generateShoppingList } from '../services/aiService';
import type { AIGeneratedItem } from '../types/ai';

interface AISuggestionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddItems: (items: AIGeneratedItem[]) => void;
}

const QUICK_SUGGESTIONS = [
  { emoji: '🥞', text: 'Crêpes pour 4 personnes' },
  { emoji: '🧀', text: 'Raclette pour 6 personnes' },
  { emoji: '🍖', text: 'Barbecue du dimanche' },
  { emoji: '🍝', text: 'Pâtes carbonara pour 2' },
  { emoji: '🥗', text: 'Salade César pour 4' },
  { emoji: '🎂', text: 'Gâteau au chocolat' },
];

export function AISuggestionModal({ isOpen, onClose, onAddItems }: AISuggestionModalProps) {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt.trim() || loading) return;

    setLoading(true);
    setError(null);

    try {
      const items = await generateShoppingList(prompt);
      
      if (items.length === 0) {
        setError('Aucun article généré. Essayez une autre formulation.');
        return;
      }

      onAddItems(items);
      setPrompt('');
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur lors de la génération';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickSuggestion = (text: string) => {
    setPrompt(text);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 p-6 flex items-center justify-between sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#FFD100] rounded-xl flex items-center justify-center">
              <Sparkles className="text-slate-900" size={24} fill="currentColor" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Assistant IA Groq</h2>
              <p className="text-gray-400 text-sm flex items-center gap-1">
                <Zap size={14} className="text-[#FFD100]" />
                Llama 3.1 70B - Ultra rapide & 100% gratuit
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center transition-colors"
          >
            <X className="text-white" size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Quick Suggestions */}
          <div className="mb-6">
            <p className="text-sm font-semibold text-gray-700 mb-3">Suggestions rapides :</p>
            <div className="grid grid-cols-2 gap-2">
              {QUICK_SUGGESTIONS.map((suggestion, index) => (
                <button
                  key={index}
                  onClick={() => handleQuickSuggestion(suggestion.text)}
                  className="px-4 py-3 bg-slate-50 hover:bg-[#FFD100] hover:text-slate-900 rounded-xl text-left transition-colors group"
                >
                  <span className="text-2xl mr-2">{suggestion.emoji}</span>
                  <span className="text-sm font-medium group-hover:font-bold">{suggestion.text}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Input Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Ou décrivez votre besoin :
              </label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Ex: Repas italien pour 4 personnes, Petit-déjeuner équilibré, Apéro entre amis..."
                className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FFD100] focus:border-transparent resize-none"
                rows={3}
                disabled={loading}
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
                <p className="text-red-700 text-sm font-semibold">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={!prompt.trim() || loading}
              className="w-full bg-gradient-to-r from-slate-900 to-slate-800 hover:from-slate-800 hover:to-slate-700 text-white font-bold py-4 px-6 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 shadow-lg"
            >
              {loading ? (
                <>
                  <Loader className="animate-spin" size={20} />
                  <span>Génération en cours...</span>
                </>
              ) : (
                <>
                  <Sparkles size={20} fill="currentColor" />
                  <span>Générer ma liste</span>
                </>
              )}
            </button>
          </form>

          {/* Info */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
            <p className="text-sm text-blue-800">
              <strong>💡 Astuce :</strong> Plus votre description est précise (nombre de personnes, type de repas), 
              meilleurs seront les résultats avec les quantités adaptées !
            </p>
          </div>
          
          {/* Groq Info Banner */}
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-xl">
            <p className="text-xs text-green-800">
              ⚡ <strong>Groq Llama 3.1 70B</strong> : 100% gratuit, ultra-rapide (300-800 tokens/sec), sécurisé via backend
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
